import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  DollarSign, Plus, Edit2, Trash2, X, Save,
  Search, RefreshCw, Calendar, TrendingUp
} from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const ExchangeRates = () => {
  const [loading,       setLoading]       = useState(true);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [filteredRates, setFilteredRates] = useState([]);
  const [currencies,    setCurrencies]    = useState([]);
  const [baseCurrency,  setBaseCurrency]  = useState(null);

  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors,        setErrors]        = useState({});

  const [formData, setFormData] = useState({
    exchangerateid: null, fromcurrencyid: '', tocurrencyid: '',
    rate: '', effectivedate: new Date().toISOString().split('T')[0],
  });

  const [searchTerm,     setSearchTerm]     = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo,   setFilterDateTo]   = useState('');

  const fromCurrencyRef = useRef(null);

  const loadCurrencies = async () => {
    try {
      const res = await apiClient.get('/Currencies.php', { params: { active: true } });
      if (res.data.success) {
        const list = res.data.data || [];
        setCurrencies(list);
        const base = list.find(c => c.statusbasecurrency === 1);
        if (base) setBaseCurrency(base);
        else alert('No base currency found. Please set a base currency in the Currencies master.');
      }
    } catch { alert('Error loading currencies.'); }
  };

  const loadExchangeRates = async () => {
    try {
      const res = await apiClient.get('/ExchangeRates.php');
      if (res.data.success) setExchangeRates(res.data.data || []);
    } catch { alert('Error loading exchange rates.'); }
  };

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try { await loadCurrencies(); await loadExchangeRates(); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const filterExchangeRates = useCallback(() => {
    let f = [...exchangeRates];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      f = f.filter(r =>
        r.fromcurrencyname?.toLowerCase().includes(q) ||
        r.tocurrencyname?.toLowerCase().includes(q) ||
        r.fromcurrencysymbol?.toLowerCase().includes(q) ||
        r.tocurrencysymbol?.toLowerCase().includes(q) ||
        r.rate?.toString().includes(searchTerm)
      );
    }
    if (filterCurrency) f = f.filter(r => r.fromcurrencyid === parseInt(filterCurrency));
    if (filterDateFrom) f = f.filter(r => new Date(r.effectivedate) >= new Date(filterDateFrom));
    if (filterDateTo)   f = f.filter(r => new Date(r.effectivedate) <= new Date(filterDateTo));
    setFilteredRates(f);
  }, [exchangeRates, searchTerm, filterCurrency, filterDateFrom, filterDateTo]);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);
  useEffect(() => { filterExchangeRates(); }, [filterExchangeRates]);

  const handleAdd = () => {
    if (!baseCurrency) { alert('Please set a base currency first in the Currencies master.'); return; }
    setFormData({ exchangerateid: null, fromcurrencyid: '', tocurrencyid: baseCurrency.currencyid, rate: '', effectivedate: new Date().toISOString().split('T')[0] });
    setErrors({}); setEditMode(false); setShowModal(true);
    setTimeout(() => fromCurrencyRef.current?.focus(), 100);
  };

  const handleEdit = (rate) => {
    setFormData({ exchangerateid: rate.exchangerateid, fromcurrencyid: rate.fromcurrencyid, tocurrencyid: rate.tocurrencyid, rate: rate.rate, effectivedate: rate.effectivedate });
    setErrors({}); setEditMode(true); setShowModal(true);
    setTimeout(() => fromCurrencyRef.current?.focus(), 100);
  };

  const handleDelete = async (rate) => {
    if (!window.confirm(`Delete exchange rate for ${rate.fromcurrencyname}?`)) return;
    try {
      const res = await apiClient.delete('/ExchangeRates.php', { data: { exchangerateid: rate.exchangerateid } });
      if (res.data.success) { alert('Exchange rate deleted successfully!'); loadExchangeRates(); }
      else alert(res.data.message || 'Failed to delete exchange rate');
    } catch (err) { alert('Error deleting exchange rate: ' + (err.response?.data?.message || err.message)); }
  };

  const handleInputChange = (field, value) => {
    setFormData(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: null }));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.fromcurrencyid) errs.fromcurrencyid = 'From currency is required';
    if (formData.fromcurrencyid === formData.tocurrencyid) errs.fromcurrencyid = 'From and To currencies must be different';
    if (!formData.rate || parseFloat(formData.rate) <= 0) errs.rate = 'Valid exchange rate is required';
    if (!formData.effectivedate) errs.effectivedate = 'Effective date is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { fromcurrencyid: parseInt(formData.fromcurrencyid), tocurrencyid: parseInt(formData.tocurrencyid), rate: parseFloat(formData.rate), effectivedate: formData.effectivedate };
      if (editMode) payload.exchangerateid = formData.exchangerateid;
      const res = await apiClient[editMode ? 'put' : 'post']('/ExchangeRates.php', payload);
      if (res.data.success) {
        alert(`Exchange rate ${editMode ? 'updated' : 'created'} successfully!`);
        setShowModal(false); loadExchangeRates();
      } else { alert(res.data.message || `Failed to ${editMode ? 'update' : 'create'} exchange rate`); }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      if (msg.includes('already exists')) setErrors(p => ({ ...p, effectivedate: 'Exchange rate already exists for this date' }));
      else alert('Error saving exchange rate: ' + msg);
    } finally { setSubmitLoading(false); }
  };

  const handleRefresh = async () => { await loadExchangeRates(); alert('Exchange rates refreshed successfully!'); };
  const clearFilters  = () => { setSearchTerm(''); setFilterCurrency(''); setFilterDateFrom(''); setFilterDateTo(''); };

  const getCurrencyDisplay = (id) => {
    const c = currencies.find(c => c.currencyid === parseInt(id));
    return c ? `${c.currencyname} (${c.symbol})` : '';
  };

  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-gray-400 text-lg font-semibold">Loading Exchange Rates...</div>
      </div>
    );
  }

  const hasFilters = searchTerm || filterCurrency || filterDateFrom || filterDateTo;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <DollarSign size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Exchange Rates Management</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage currency exchange rates</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/15 border border-white/35 rounded-lg text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-white/25"
            style={{ color: '#fff' }}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
            style={{ color: NAVY }}>
            <Plus size={17} /> Add Exchange Rate
          </button>
        </div>
      </div>

      <div className="flex-1 p-5 flex flex-col gap-5">

        {/* Base currency banner */}
        {baseCurrency && (
          <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <TrendingUp size={18} className="text-emerald-700 flex-shrink-0" />
            <span className="text-emerald-700 font-semibold text-sm">
              Base Currency: {baseCurrency.currencyname} ({baseCurrency.symbol})
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Rates',       value: exchangeRates.length },
            { label: 'Filtered Results',  value: filteredRates.length },
            { label: 'Active Currencies', value: currencies.filter(c => c.isactive === 1).length },
          ].map(({ label, value }) => (
            <div key={label} className="px-5 py-4 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="text-[13px] text-gray-500 mb-1">{label}</div>
              <div className="text-[28px] font-bold" style={{
                background: `linear-gradient(135deg, ${NAVY} 0%, #3b82f6 100%)`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 bg-white border border-gray-200 rounded-xl">
          {/* Search */}
          <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
            focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
            <Search size={16} className="text-gray-400 flex-shrink-0" />
            <input type="text" placeholder="Search by currency name, symbol, or rate..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Currency filter */}
          <select value={filterCurrency} onChange={e => setFilterCurrency(e.target.value)}
            className="px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]">
            <option value="">All Currencies</option>
            {currencies.filter(c => c.currencyid !== baseCurrency?.currencyid).map(c => (
              <option key={c.currencyid} value={c.currencyid}>{c.currencyname} ({c.symbol})</option>
            ))}
          </select>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <Calendar size={15} className="text-gray-400 flex-shrink-0" />
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none focus:border-[#0d1c6b]" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none focus:border-[#0d1c6b]" />
          </div>

          {hasFilters && (
            <button onClick={clearFilters}
              className="px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 hover:border-gray-300">
              Clear Filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full border-collapse">
            <thead>
              <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                {['From Currency', 'To Currency', 'Exchange Rate', 'Effective Date', 'Last Updated', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                    style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRates.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-16 text-gray-400">
                    <div className="flex flex-col items-center gap-3">
                      {exchangeRates.length === 0 ? (
                        <>
                          <DollarSign size={48} className="opacity-20" />
                          <div>No exchange rates found. Click "Add Exchange Rate" to create one.</div>
                        </>
                      ) : (
                        <>
                          <Search size={48} className="opacity-20" />
                          <div>No exchange rates match your filters.</div>
                          <button onClick={clearFilters}
                            className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0 mt-1">
                            Clear filters
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRates.map(rate => (
                  <tr key={rate.exchangerateid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 text-white text-[12px] font-semibold rounded"
                          style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                          {rate.fromcurrencyname}
                        </span>
                        <span className="text-[12px] text-gray-500 font-semibold">{rate.fromcurrencysymbol}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 text-white text-[12px] font-semibold rounded"
                          style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                          {rate.tocurrencyname}
                        </span>
                        <span className="text-[12px] text-gray-500 font-semibold">{rate.tocurrencysymbol}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-blue-700 text-[15px]">
                      {parseFloat(rate.rate).toFixed(6)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">
                      {new Date(rate.effectivedate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {rate.updatedat ? new Date(rate.updatedat).toLocaleDateString('en-GB') : '-'}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(rate)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(rate)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[480px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <DollarSign size={18} />
                {editMode ? 'Edit Exchange Rate' : 'Add New Exchange Rate'}
              </h3>
              <button onClick={() => setShowModal(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* From Currency */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  From Currency <span className="text-red-500">*</span>
                </label>
                <select ref={fromCurrencyRef} value={formData.fromcurrencyid}
                  onChange={e => handleInputChange('fromcurrencyid', e.target.value)}
                  className={selectCls(errors.fromcurrencyid)}>
                  <option value="">-- Select Currency --</option>
                  {currencies.filter(c => c.currencyid !== baseCurrency?.currencyid && c.isactive === 1).map(c => (
                    <option key={c.currencyid} value={c.currencyid}>{c.currencyname} ({c.symbol})</option>
                  ))}
                </select>
                {errors.fromcurrencyid && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.fromcurrencyid}</p>}
              </div>

              {/* To Currency (read-only) */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  To Currency (Base) <span className="text-red-500">*</span>
                </label>
                <input type="text" value={getCurrencyDisplay(formData.tocurrencyid)} readOnly
                  className="w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-100 cursor-not-allowed outline-none" />
                <p className="text-[11px] text-gray-400 mt-1">Base currency is set in Currencies master</p>
              </div>

              {/* Rate */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Exchange Rate <span className="text-red-500">*</span>
                </label>
                <input type="number" step="0.000001" value={formData.rate}
                  onChange={e => handleInputChange('rate', e.target.value)}
                  placeholder="e.g., 1.085000"
                  className={inputCls(errors.rate)} />
                {errors.rate && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.rate}</p>}
                <p className="text-[11px] text-gray-400 mt-1">Rate must be greater than 0 (6 decimal places)</p>
              </div>

              {/* Effective Date */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Effective Date <span className="text-red-500">*</span>
                </label>
                <input type="date" value={formData.effectivedate}
                  onChange={e => handleInputChange('effectivedate', e.target.value)}
                  className={inputCls(errors.effectivedate)} />
                {errors.effectivedate && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.effectivedate}</p>}
                <p className="text-[11px] text-gray-400 mt-1">Date from which this rate is effective</p>
              </div>

              {/* Rate preview */}
              {formData.fromcurrencyid && formData.rate && (
                <div className="mb-5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-[13px] font-semibold text-blue-800 mb-1">Rate Preview:</p>
                  <p className="text-[14px] text-blue-700">
                    1 {currencies.find(c => c.currencyid === parseInt(formData.fromcurrencyid))?.symbol} = {parseFloat(formData.rate || 0).toFixed(6)} {baseCurrency?.symbol}
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  <Save size={16} />
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default ExchangeRates;